import React, { useEffect, useImperativeHandle } from 'react'
import { useDispatch } from 'react-redux'
import { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit'
import { Col, Form, Offcanvas, Row } from 'react-bootstrap'
import { useField, useFormikContext } from 'formik'

import { shouldShowExperimentalFeatures, useAuth } from '../../auth'
import { modifyArticle } from '../articleAPI'
import { getNumberOfRows, stripTags } from '../text'
import { changeParagraphStyleMultiline, getParagraphStyleName, getSubmitParamForArticleUpsert, TagEditor } from '..'
import { useFormikReset, DirtyEffect } from '../CommonComponents'
import { ArticleNameTitle } from '../ArticleNameTitle'
import { Article, Layoutable } from '../types'

interface FormValues {
  bodyContent: string | null
  bodyTaggedContent: string | null
  bodyNumberOfRows: number | null
}

type EventHandler = {
  onSubmit: (values: FormValues) => Promise<void>
}

const NoBodyField = () => {
  return <>本文なし</>
}

interface BodyEditFieldProps {
  layoutable: Layoutable
  article: Article
  inputDisabled: boolean
}

const BodyEditField: React.FC<BodyEditFieldProps> = ({ layoutable, article, inputDisabled }) => {
  const auth = useAuth()
  const [, contentMeta, contentHelpers] = useField<string | null>('bodyContent')
  const [taggedField, taggedMeta, taggedHelpers] = useField<string | null>('bodyTaggedContent')
  const [numOfRowsField, , numOfRowsHelpers] = useField<number | null>('bodyNumberOfRows')
  const { resetCount } = useFormikReset()

  const isEq = (a: string, b: string): boolean => {
    const aHtml = a.replace('<sr />', '<br />').replace(/\n/g, '')
    const bHtml = b.replace('<sr />', '<br />').replace(/\n/g, '')
    const aDiv = document.createElement('div')
    aDiv.innerHTML = aHtml
    const bDiv = document.createElement('div')
    bDiv.innerHTML = bHtml
    return aDiv.isEqualNode(bDiv)
  }

  const dirty = !isEq(taggedMeta.initialValue ?? '', taggedField.value ?? '')
  const editable = !inputDisabled && shouldShowExperimentalFeatures(auth)

  const onChange = (taggedContent: string, _old: string | undefined) => {
    const content = stripTags(taggedContent)
    contentHelpers.setValue(content ?? null, true)
    taggedHelpers.setValue(taggedContent, true)

    const numberOfCharactersPerRow = layoutable?.numberOfCharactersPerRow
    const bodyNumberOfRows = !content ? null : getNumberOfRows(numberOfCharactersPerRow, content, false)
    numOfRowsHelpers.setValue(bodyNumberOfRows ?? null)
  }

  return (
    <>
      <Row className='mb-2'>
        <Col>
          <span className='body-number-of-rows fs-5'>本文行数: {numOfRowsField.value}</span>
          {!!article.rows && <span className='article-rows text-secondary'>（行数指定: {article.rows}行）</span>}
        </Col>
      </Row>
      <Row>
        <Col>
          <DirtyEffect dirty={dirty}>
            <TagEditor
              key={resetCount}
              initialTaggedContent={taggedField.value ?? undefined}
              styleTagType='sentence'
              setting={layoutable.setting}
              onSubmit={(_e) => undefined}
              disabled={inputDisabled}
              editable={editable}
              onChange={onChange}
              allowLineBreak
              allowMultiParagraph
              appearance='withoutSubmit'
            />
            <Form.Group as={Col}>
              <Form.Control isInvalid={!!contentMeta.error} style={{ display: 'none' }} />
              <Form.Control.Feedback type='invalid'>{contentMeta.error}</Form.Control.Feedback>
            </Form.Group>
          </DirtyEffect>
        </Col>
      </Row>
    </>
  )
}

interface ArticleBodyDetailBodyProps {
  layoutable: Layoutable
  article: Article
  inputDisabled: boolean
}

const ArticleBodyDetailBody: React.FC<ArticleBodyDetailBodyProps> = ({ layoutable, article, inputDisabled }) => {
  const [contentField] = useField<string | null>('bodyContent')

  const child =
    contentField.value === null ? (
      <NoBodyField />
    ) : (
      <BodyEditField layoutable={layoutable} article={article} inputDisabled={inputDisabled} />
    )

  return <>{child}</>
}

interface Props {
  article: Article
  layoutable: Layoutable
  layoutableId: string | number
  layoutableType: string
  enumValues: unknown
  isSelectedLayoutableWritable: boolean
  eventHandlerRef: React.RefObject<EventHandler | null>
}

export const ArticleBodyDetail: React.FC<Props> = ({
  article,
  layoutable,
  layoutableId,
  layoutableType,
  isSelectedLayoutableWritable,
  eventHandlerRef
}) => {
  const inputDisabled = !isSelectedLayoutableWritable || !!article.layoutLocked

  const commonProps = {
    article,
    inputDisabled,
    layoutable
  }

  const numberOfCharactersPerRow = layoutable?.numberOfCharactersPerRow

  const isBodyComplete =
    article.bodyTaggedContent !== null && article.bodyTaggedContent !== null && numberOfCharactersPerRow !== null

  const context = useFormikContext<Partial<FormValues>>()
  const formikReset = useFormikReset()
  const dispatch = useDispatch<ThunkDispatch<unknown, unknown, UnknownAction>>()

  useEffect(() => {
    const initialValues = isBodyComplete
      ? {
          bodyContent: stripTags(article.bodyTaggedContent!.replace(/\n/g, '')),
          bodyTaggedContent: article.bodyTaggedContent!.replace(/\n/g, ''),
          bodyNumberOfRows: getNumberOfRows(numberOfCharactersPerRow, article.bodyContent, false)
        }
      : {
          bodyContent: null,
          bodyTaggedContent: null,
          bodyNumberOfRows: null
        }

    context.resetForm({ values: initialValues })

    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [])

  useImperativeHandle(eventHandlerRef, () => ({
    onSubmit: async (values: FormValues) => {
      const paragraphStyleName = getParagraphStyleName(layoutable.setting, 'body') ?? ''
      const numberOfCharactersPerRow = layoutable?.numberOfCharactersPerRow
      const bodyNumberOfRows = !values.bodyContent
        ? null
        : getNumberOfRows(numberOfCharactersPerRow, values.bodyTaggedContent, true)
      const bodyTaggedContent = changeParagraphStyleMultiline(values.bodyTaggedContent!, paragraphStyleName)
      const bodyContent = stripTags(bodyTaggedContent)
      const newArticle = {
        ...article,
        bodyContent,
        bodyTaggedContent,
        bodyNumberOfRows
      }
      const props = {
        layoutableType,
        layoutableId,
        layoutableLockVersion: layoutable?.lockVersion,
        article: newArticle,
        loadedBinaryFile: undefined,
        deleteFile: undefined,
        auxiliaryLine: null,
        successAlertBody: undefined
      }
      const submitParam = getSubmitParamForArticleUpsert(props) as Parameters<typeof modifyArticle>[0]

      return dispatch(modifyArticle(submitParam)).then(() => {
        formikReset.resetForm({ values: context.values })
      })
    }
  }))

  if ((context.values as Partial<FormValues> | undefined)?.bodyContent === undefined) {
    return undefined
  }

  return (
    <>
      <Offcanvas.Header>
        <Offcanvas.Title className='detail-offcanvas-title'>
          本文
          <ArticleNameTitle article={article} />
        </Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body className='article-body-detail-offcanvas-body'>
        <ArticleBodyDetailBody {...commonProps} />
      </Offcanvas.Body>
    </>
  )
}
